// Page de résultats de recherche AbracadaWorld
// Affiche les résultats selon les filtres du moteur de recherche

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, MapPin, Filter, Grid3X3, List, SlidersHorizontal,
  Star, Store, Users, ShoppingBag, Building, Briefcase, Plane,
  ChevronRight, X, ArrowLeft, Heart, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// Données de démonstration
const generateMockResults = (type, universe, count = 12) => {
  const results = [];
  for (let i = 1; i <= count; i++) {
    results.push({
      id: i,
      title: `${type === 'professionals' ? 'Professionnel' : 'Produit'} ${universe || 'Multi-univers'} #${i}`,
      description: `Description pour ${type} dans l'univers ${universe || 'tous'}`,
      price: type !== 'professionals' ? Math.floor(Math.random() * 500) + 50 : null,
      rating: (4 + Math.random()).toFixed(1),
      reviews: Math.floor(Math.random() * 200) + 10,
      location: ['Paris', 'Lyon', 'Marseille', 'Bordeaux', 'Lille'][Math.floor(Math.random() * 5)],
      verified: Math.random() > 0.5,
      type,
      universe
    });
  }
  return results;
};

// Types de recherche avec icônes
const SEARCH_TYPES = {
  'prestations': { label: 'Prestations', icon: Users, color: '#F97316' },
  'produits-pro': { label: 'Produits Pro', icon: Store, color: '#3B82F6' },
  'produits-particuliers': { label: 'Particuliers', icon: ShoppingBag, color: '#22C55E' },
  'immobilier': { label: 'Immobilier', icon: Building, color: '#7C3AED' },
  'emploi': { label: 'Emploi', icon: Briefcase, color: '#059669' },
  'voyage': { label: 'Voyage', icon: Plane, color: '#14B8A6' }
};

// Univers disponibles
const UNIVERSES = [
  { id: 'batiment', name: 'Bâtiment', color: '#F97316' },
  { id: 'maison', name: 'Maison', color: '#8B5CF6' },
  { id: 'jardin', name: 'Jardin', color: '#22C55E' },
  { id: 'piscine', name: 'Piscine & Spa', color: '#06B6D4' },
  { id: 'mode', name: 'Mode', color: '#EC4899' },
  { id: 'beaute', name: 'Beauté', color: '#F472B6' },
  { id: 'voiture', name: 'Voiture', color: '#3B82F6' },
  { id: 'bateau', name: 'Bateau', color: '#0EA5E9' },
  { id: 'jouets', name: 'Jouets', color: '#EAB308' },
  { id: 'voyage', name: 'Voyage', color: '#14B8A6' },
  { id: 'coin-secret', name: 'Coin Secret', color: '#DC2626' },
  { id: 'immobilier', name: 'Immobilier', color: '#7C3AED' },
  { id: 'emploi', name: 'Emploi', color: '#059669' },
  { id: 'electronique', name: 'Électronique', color: '#6366F1' },
  { id: 'loisirs', name: 'Loisirs', color: '#A855F7' },
  { id: 'sport', name: 'Sport', color: '#EF4444' }
];

// Composant Carte Résultat
function ResultCard({ result, viewMode }) {
  const typeConfig = SEARCH_TYPES[result.type] || SEARCH_TYPES['prestations'];
  const universeConfig = UNIVERSES.find(u => u.id === result.universe);
  const color = universeConfig?.color || typeConfig.color;

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-all flex gap-4"
      >
        <div 
          className="w-24 h-24 rounded-lg flex-shrink-0 flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Package size={32} style={{ color: `${color}80` }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-bold text-gray-900 truncate">{result.title}</h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{result.description}</p>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-1">
                  <Star size={14} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-sm font-medium">{result.rating}</span>
                  <span className="text-sm text-gray-400">({result.reviews})</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <MapPin size={14} />
                  {result.location}
                </div>
                {result.verified && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Vérifié</span>
                )}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              {result.price && (
                <p className="text-xl font-bold" style={{ color }}>{result.price}€</p>
              )}
              <Button size="sm" className="mt-2" style={{ backgroundColor: color }}>
                Voir
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group"
    >
      <div 
        className="h-40 relative"
        style={{ backgroundColor: `${color}20` }}
      >
        <div className="w-full h-full flex items-center justify-center">
          <Package size={48} style={{ color: `${color}40` }} />
        </div>
        {result.verified && (
          <div className="absolute top-3 right-3 px-2 py-1 bg-green-500 text-white text-xs rounded-full">
            Vérifié
          </div>
        )}
        <button className="absolute top-3 left-3 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          <Heart size={16} className="text-gray-600" />
        </button>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-gray-900 truncate">{result.title}</h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{result.description}</p>
        <div className="flex items-center gap-2 mt-3">
          <Star size={14} className="text-yellow-500 fill-yellow-500" />
          <span className="text-sm font-medium">{result.rating}</span>
          <span className="text-sm text-gray-400">({result.reviews} avis)</span>
        </div>
        <div className="flex items-center justify-between mt-4">
          {result.price ? (
            <span className="text-lg font-bold" style={{ color }}>{result.price}€</span>
          ) : (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <MapPin size={14} />
              {result.location}
            </span>
          )}
          <Button size="sm" variant="outline" style={{ borderColor: color, color }}>
            Détails
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// Page de recherche
export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Récupérer les paramètres
  const type = searchParams.get('type') || 'prestations';
  const universe = searchParams.get('univers') || '';
  const category = searchParams.get('categorie') || '';
  const location = searchParams.get('lieu') || '';

  const typeConfig = SEARCH_TYPES[type] || SEARCH_TYPES['prestations'];
  const universeConfig = UNIVERSES.find(u => u.id === universe);

  useEffect(() => {
    // Simuler le chargement
    setLoading(true);
    setTimeout(() => {
      setResults(generateMockResults(type, universe, 18));
      setLoading(false);
    }, 500);
  }, [type, universe, category, location]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const activeFiltersCount = [type !== 'prestations', universe, category, location].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold">A</span>
              </div>
            </Link>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-2xl relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <Input
                placeholder="Rechercher..."
                className="pl-10 bg-gray-50"
              />
            </div>

            <div className="flex items-center gap-3">
              <Link to="/reseau/connexion">
                <Button variant="ghost" size="sm">Connexion</Button>
              </Link>
              <Link to="/espace-client">
                <Button size="sm" className="bg-gradient-to-r from-violet-600 to-purple-600">
                  Espace Pro
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Breadcrumb & Title */}
          <div className="mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <Link to="/" className="hover:text-violet-600">Accueil</Link>
              <ChevronRight size={14} />
              <span>Recherche</span>
              {universe && (
                <>
                  <ChevronRight size={14} />
                  <span className="text-gray-900">{universeConfig?.name}</span>
                </>
              )}
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              {typeConfig.label} {universe ? `- ${universeConfig?.name}` : ''}
            </h1>
            <p className="text-gray-500 mt-1">
              {results.length} résultats trouvés {location && `à ${location}`}
            </p>
          </div>

          <div className="flex gap-6">
            {/* Sidebar Filters */}
            <aside className={`w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-white rounded-xl border border-gray-200 p-4 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Filtres</h3>
                  {activeFiltersCount > 0 && (
                    <button onClick={clearFilters} className="text-sm text-violet-600 hover:text-violet-700">
                      Effacer ({activeFiltersCount})
                    </button>
                  )}
                </div>

                {/* Type de recherche */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Type</h4>
                  <div className="space-y-2">
                    {Object.entries(SEARCH_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => updateFilter('type', key)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                            type === key
                              ? 'bg-violet-50 text-violet-700'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <Icon size={16} />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Univers */}
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Univers</h4>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    <button
                      onClick={() => updateFilter('univers', '')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        !universe ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Tous les univers
                    </button>
                    {UNIVERSES.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => updateFilter('univers', u.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                          universe === u.id ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: u.color }} />
                        {u.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Localisation */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Localisation</h4>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <Input
                      value={location}
                      onChange={(e) => updateFilter('lieu', e.target.value)}
                      placeholder="Ville ou CP"
                      className="pl-9 text-sm"
                    />
                  </div>
                </div>
              </div>
            </aside>

            {/* Results */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {/* Active Filters Tags */}
                  {universe && (
                    <span 
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                      style={{ backgroundColor: `${universeConfig?.color}20`, color: universeConfig?.color }}
                    >
                      {universeConfig?.name}
                      <button onClick={() => updateFilter('univers', '')}>
                        <X size={14} />
                      </button>
                    </span>
                  )}
                  {location && (
                    <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                      <MapPin size={14} />
                      {location}
                      <button onClick={() => updateFilter('lieu', '')}>
                        <X size={14} />
                      </button>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <SlidersHorizontal size={18} />
                    Filtres
                  </button>
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    >
                      <Grid3X3 size={18} className={viewMode === 'grid' ? 'text-violet-600' : 'text-gray-400'} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    >
                      <List size={18} className={viewMode === 'list' ? 'text-violet-600' : 'text-gray-400'} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Results Grid/List */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
                  <Search size={48} className="mx-auto text-gray-300 mb-4" />
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Aucun résultat</h2>
                  <p className="text-gray-500 mb-6">Essaie de modifier tes critères de recherche</p>
                  <Button onClick={clearFilters} variant="outline">
                    Effacer les filtres
                  </Button>
                </div>
              ) : (
                <div className={viewMode === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' 
                  : 'space-y-4'
                }>
                  {results.map((result, index) => (
                    <ResultCard key={result.id} result={result} viewMode={viewMode} />
                  ))}
                </div>
              )}

              {/* Load More */}
              {results.length > 0 && (
                <div className="text-center mt-8">
                  <Button variant="outline" size="lg">
                    Charger plus de résultats
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
